import React, { useState, useEffect } from 'react';
import { BookOpen, Book, FileText, Share2, Edit3, Star, X, Smartphone, Info, Tag, Menu, Sparkles, Keyboard, BookMarked, Youtube } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import '../i18n';

// Pour manipuler le head
import { Helmet } from 'react-helmet-async';
import HeroCarousel from './hero_carousel.tsx';
import DailyCommentaryCard from './daily_commentary_card.tsx';
import CommunityLinks, { HeroDownloadCta } from './community_links.tsx';

const HomePage = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { lang } = useParams();

  // Set the language based on the URL parameter with proper error handling
  useEffect(() => {
    // Make sure language actually changes based on URL parameter
    if (lang && ['fr', 'en', 'it', 'es'].includes(lang)) {
      // Handle loading failure by falling back to French
      i18n.changeLanguage(lang).catch(error => {
        console.warn(`Failed to load ${lang} translations, falling back to French`, error);
        i18n.changeLanguage('fr');
      });
    }
  }, [lang, i18n]);

  // Set language prefix based on URL parameter or default to French
  const langPrefix = lang ? `/${lang}` : '/fr';

  // Détermination de l'URL canonique
  const canonicalUrl = `https://bible4.me${langPrefix}/`;


  // Simplified components for the preview
  interface FeatureCardProps {
    icon: React.ReactNode;
    title: string;
    description: string;
  }

  const FeatureCard = ({ icon, title, description }: FeatureCardProps) => (
    <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
      <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center text-amber-900 mb-4">
        {icon}
      </div>
      <h3 className="text-xl font-bold text-amber-900 mb-2">{title}</h3>
      <p className="text-amber-800">{description}</p>
    </div>
  );

  interface StepCardProps {
    number: number;
    title: string;
    description: string;
  }

  const StepCard = ({ number, title, description }: StepCardProps) => (
    <div className="text-center">
      <div className="w-12 h-12 bg-amber-900 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
        {number}
      </div>
      <h3 className="text-xl font-bold text-amber-900 mb-2">{title}</h3>
      <p className="text-amber-800">{description}</p>
    </div>
  );
  // Add handler for beta-subscription route
  const handleDownloadApp = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    navigate(`${langPrefix}/download`);
  };
  const handleBeta = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    navigate(`${langPrefix}/download`);
  };

  const handleFeatures = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    navigate(`${langPrefix}/features`);
  };
  
  const handleWelcome = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    navigate(`${langPrefix}/welcome`);
  };

  const handleAboutUs = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    navigate(`${langPrefix}/about-us`);
  };
  const handleEvangile = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    navigate(`${langPrefix}/evangile`);
  };
  const handleBibleViewer = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    navigate(`${langPrefix}/bible-content`);
  };

  const handleVideos = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    navigate(`${langPrefix}/videos`);
  };

  const handlePrivacyPolicy = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    navigate(`${langPrefix}/privacy`);
  };

  const handleDocs = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    navigate(`${langPrefix}/docs/`);
  };

  const handleVersions = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    navigate(`${langPrefix}/features`);
  };

  const AppBanner = () => {
    if (!isVisible) return null;

    return (
      <div className="bg-white shadow-lg z-50 w-full">
        <div className="flex items-center p-3 max-w-6xl mx-auto">
          <div className="w-12 h-12 flex items-center justify-center mr-3">
            <img
              src="/logo%20626x626.png"
              alt="Bible4Me Logo"
              className="w-full h-full object-contain rounded-lg"
            />
          </div>

          <div className="flex-1">
            <h3 className="font-semibold text-amber-900">{t('home.title')}</h3>
            <div className="flex items-center gap-2">
              <div className="flex">
                {[1, 2, 3, 4, 5].map((_, i) => (
                  <Star key={i} size={12} className="text-amber-400" />
                ))}
              </div>
              <span className="text-xs text-amber-800">{t('home.appBanner.free', 'Gratuit')}</span>
            </div>
          </div>

          <div className="flex items-center gap-2">            <a
              href={`${langPrefix}/download`}
              onClick={handleDownloadApp}
              className="bg-amber-100 text-amber-900 px-4 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1 hover:bg-amber-200 transition-colors"
            >
              <Smartphone size={16} />
              <span>{t('home.downloadApp', 'Télécharger')}</span>
            </a>
            <button
              onClick={() => setIsVisible(false)}
              className="p-1.5 text-amber-900 hover:bg-amber-100 rounded-full transition-colors"
              aria-label={t('common.close', 'Fermer') || 'Fermer'}
            >
              <X size={20} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>{t('appTitle')}</title>
        <meta name="description" content={t('appDescription')} />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>
      <div className="bg-amber-50">
      <AppBanner />

      {/* Navigation Links */}
      <div className="bg-amber-900 text-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex justify-between items-center py-2">
            <div></div>
            {/* Desktop nav */}
            <div className="hidden md:flex items-center space-x-6">
              <a href={`${langPrefix}/welcome`} onClick={handleWelcome} className="flex items-center gap-1 text-sm hover:text-amber-200 transition-colors">
                <BookOpen size={16} />
                <span>{t('welcome.title', 'Welcome')}</span>
              </a>
              <a href={`${langPrefix}/about-us`} onClick={handleAboutUs} className="flex items-center gap-1 text-sm hover:text-amber-200 transition-colors">
                <Info size={16} />
                <span>{t('about.title', 'About Us')}</span>
              </a>
              <a href={`${langPrefix}/docs`} onClick={handleDocs} className="flex items-center gap-1 text-sm hover:text-amber-200 transition-colors" title={t('home.docs.linkTooltip', 'Read the user documentation') || undefined}>
                <FileText size={16} />
                <span>{t('home.docs.link', 'Documentation')}</span>
              </a>
              <a href={`${langPrefix}/features`} onClick={handleVersions} className="flex items-center gap-1 text-sm hover:text-amber-200 transition-colors" title={t('home.versions.linkTooltip', 'See release notes and version history') || undefined}>
                <Tag size={16} />
                <span>{t('home.versions.link', 'Versions')}</span>
              </a>
              <a href={`${langPrefix}/evangile`} onClick={handleEvangile} className="flex items-center gap-1 text-sm hover:text-amber-200 transition-colors">
                <BookOpen size={16} />
                <span>{t('home.nav.evangile', 'Commentaire du jour')}</span>
              </a>
              <a href={`${langPrefix}/bible-content`} onClick={handleBibleViewer} className="flex items-center gap-1 text-sm hover:text-amber-200 transition-colors">
                <Book size={16} />
                <span>{t('home.nav.bible', 'Lire la Bible')}</span>
              </a>
              <a href={`${langPrefix}/videos`} onClick={handleVideos} className="flex items-center gap-1 text-sm hover:text-amber-200 transition-colors">
                <Youtube size={16} />
                <span>{t('home.nav.videos', 'Vidéos')}</span>
              </a>
              <a href={`${langPrefix}/download`} onClick={handleDownloadApp} className="flex items-center gap-1 text-sm hover:text-amber-200 transition-colors">
                <Smartphone size={16} />
                <span>{t('home.downloadApp', 'Download App')}</span>
              </a>
            </div>
            {/* Mobile hamburger button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-1.5 hover:bg-amber-800 rounded transition-colors"
              aria-label="Menu"
            >
              {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
          {/* Mobile dropdown menu */}
          {isMenuOpen && (
            <div data-testid="mobile-nav-dropdown" className="md:hidden border-t border-amber-800 py-2 flex flex-col gap-1">
              <a href={`${langPrefix}/welcome`} onClick={(e) => { handleWelcome(e); setIsMenuOpen(false); }} className="flex items-center gap-2 px-2 py-2 text-sm hover:bg-amber-800 rounded transition-colors">
                <BookOpen size={16} />
                <span>{t('welcome.title', 'Welcome')}</span>
              </a>
              <a href={`${langPrefix}/about-us`} onClick={(e) => { handleAboutUs(e); setIsMenuOpen(false); }} className="flex items-center gap-2 px-2 py-2 text-sm hover:bg-amber-800 rounded transition-colors">
                <Info size={16} />
                <span>{t('about.title', 'About Us')}</span>
              </a>
              <a href={`${langPrefix}/docs`} onClick={(e) => { handleDocs(e); setIsMenuOpen(false); }} className="flex items-center gap-2 px-2 py-2 text-sm hover:bg-amber-800 rounded transition-colors">
                <FileText size={16} />
                <span>{t('home.docs.link', 'Documentation')}</span>
              </a>
              <a href={`${langPrefix}/features`} onClick={(e) => { handleVersions(e); setIsMenuOpen(false); }} className="flex items-center gap-2 px-2 py-2 text-sm hover:bg-amber-800 rounded transition-colors">
                <Tag size={16} />
                <span>{t('home.versions.link', 'Versions')}</span>
              </a>
              <a href={`${langPrefix}/evangile`} onClick={(e) => { handleEvangile(e); setIsMenuOpen(false); }} className="flex items-center gap-2 px-2 py-2 text-sm hover:bg-amber-800 rounded transition-colors">
                <BookOpen size={16} />
                <span>{t('home.nav.evangile', 'Commentaire du jour')}</span>
              </a>
              <a href={`${langPrefix}/bible-content`} onClick={(e) => { handleBibleViewer(e); setIsMenuOpen(false); }} className="flex items-center gap-2 px-2 py-2 text-sm hover:bg-amber-800 rounded transition-colors">
                <Book size={16} />
                <span>{t('home.nav.bible', 'Lire la Bible')}</span>
              </a>
              <a href={`${langPrefix}/videos`} onClick={(e) => { handleVideos(e); setIsMenuOpen(false); }} className="flex items-center gap-2 px-2 py-2 text-sm hover:bg-amber-800 rounded transition-colors">
                <Youtube size={16} />
                <span>{t('home.nav.videos', 'Vidéos')}</span>
              </a>
              <a href={`${langPrefix}/download`} onClick={(e) => { handleDownloadApp(e); setIsMenuOpen(false); }} className="flex items-center gap-2 px-2 py-2 text-sm hover:bg-amber-800 rounded transition-colors">
                <Smartphone size={16} />
                <span>{t('home.downloadApp', 'Download App')}</span>
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Hero Section */}
      <section className="bg-amber-200 py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-8">
            {/* Colonne gauche : carrousel de captures */}
            <div className="order-3 lg:order-1 shrink-0">
              <HeroCarousel />
            </div>

            {/* Colonne centre : message */}
            <div className="flex-1 order-1 lg:order-2 text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-amber-900 mb-4">
                {t('home.hero.title')}
              </h1>
              <p className="text-lg text-amber-800">
                {t('home.hero.description')}
              </p>
              <HeroDownloadCta otherPlatformsHref={`${langPrefix}/download`} />
            </div>

            {/* Colonne droite : commentaire du jour */}
            <div className="order-2 lg:order-3 shrink-0">
              <DailyCommentaryCard lang={lang || 'fr'} />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-amber-900 text-center mb-12">
            {t('home.features.title', 'Découvrez la Bible autrement')}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard
              icon={<BookOpen />}
              title={t('home.features.evangile.title', 'Commentaire du jour')}
              description={t('home.features.evangile.description', 'Un réflexion quotidienne sur un passage de l\'Évangile.')}
            />
            <FeatureCard
              icon={<Sparkles />}
              title={t('home.features.aiComment.title', 'Allez plus loin avec l\'IA')}
              description={t('home.features.aiComment.description', 'Approfondissez chaque passage : l\'assistant IA éclaire le texte et enrichit vos notes d\'un commentaire.')}
            />
            <FeatureCard
              icon={<BookMarked />}
              title={t('home.features.annotations.title', 'Annotez votre Bible')}
              description={t('home.features.annotations.description', 'Gardez vos notes et réflexions directement rattachées aux versets, et retrouvez-les à chaque lecture.')}
            />
            <FeatureCard
              icon={<Edit3 />}
              title={t('home.features.interactive.title')}
              description={t('home.features.interactive.description')}
            />

            <FeatureCard
              icon={<Share2 />}
              title={t('home.features.sharing.title')}
              description={t('home.features.sharing.description')}
            />

            <FeatureCard
              icon={<Keyboard />}
              title={t('home.features.smart.title')}
              description={t('home.features.smart.description')}
            />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 bg-amber-100 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-amber-900 text-center mb-12">
            {t('home.howItWorks.title')}
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <StepCard
              number={1}
              title={t('home.howItWorks.step1.title')}
              description={t('home.howItWorks.step1.description')}
            />

            <StepCard
              number={2}
              title={t('home.howItWorks.step2.title')}
              description={t('home.howItWorks.step2.description')}
            />

            <StepCard
              number={3}
              title={t('home.howItWorks.step3.title')}
              description={t('home.howItWorks.step3.description')}
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-amber-200">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-amber-900 mb-6">
            {t('home.cta.title')}
          </h2>
          <p className="text-lg text-amber-800 mb-8 max-w-2xl mx-auto">
            {t('home.cta.description')}
          </p>

          <CommunityLinks />
        </div>
      </section>

      {/* Footer with links */}
      <footer className="bg-amber-900 text-white py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 flex items-center justify-center">
                <img
                  src="/logo%20626x626.png"
                  alt="Bible4Me Logo"
                  className="w-full h-full object-contain rounded-lg"
                />
              </div>
              <span className="font-bold">{t('home.title')}</span>
            </div>            <div className="flex flex-wrap justify-center gap-6">
              <a
                href={`${langPrefix}/welcome`}
                onClick={handleWelcome}
                className="hover:text-amber-200 transition-colors"
              >
                {t('welcome.title', 'Welcome')}
              </a>
              <a
                href={`${langPrefix}/docs/01-presentation`}
                onClick={handleDocs}
                className="hover:text-amber-200 transition-colors"
                title={t('home.docs.linkTooltip', 'Read the user documentation') || undefined}
              >
                {t('home.docs.link', 'Documentation')}
              </a>
              <a
                href={`${langPrefix}/features`}
                onClick={handleVersions}
                className="hover:text-amber-200 transition-colors"
                title={t('home.versions.linkTooltip', 'See release notes and version history') || undefined}
              >
                {t('home.versions.link', 'Versions')}
              </a>
              <a
                href={`${langPrefix}/evangile`}
                onClick={handleEvangile}
                className="hover:text-amber-200 transition-colors"
              >
                {t('home.nav.evangile', 'Commentaire du jour')}
              </a>
              <a
                href={`${langPrefix}/bible-content`}
                onClick={handleBibleViewer}
                className="hover:text-amber-200 transition-colors"
              >
                {t('home.nav.bible', 'Lire la Bible')}
              </a>
              <a
                href={`${langPrefix}/videos`}
                onClick={handleVideos}
                className="hover:text-amber-200 transition-colors"
              >
                {t('home.nav.videos', 'Vidéos')}
              </a>
              <a
                href={`${langPrefix}/privacy`}
                onClick={handlePrivacyPolicy}
                className="hover:text-amber-200 transition-colors"
              >
                {t('privacy.title')}
              </a>
              <a
                href={`${langPrefix}/about-us`}
                onClick={handleAboutUs}
                className="hover:text-amber-200 transition-colors"
              >
                {t('about.title', 'About Us')}
              </a>
              <a
                href={`${langPrefix}/download`}
                onClick={handleDownloadApp}
                className="hover:text-amber-200 transition-colors"
              >
                {t('home.downloadApp')}
              </a>
            </div>
          </div>

          <div className="mt-8 text-center text-amber-300 text-sm">
            <p>{t('home.cta.title', '© 2025 Bible4Me. Tous droits réservés.')}</p>
          </div>
        </div>
      </footer>
      </div>
    </>
  );
};

export default HomePage;